import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { platformLogin } from '@/services/api'
import { AppButton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

const STYLES: Record<string, CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns: '1fr 1.2fr',
    background: 'var(--bg)',
  },
  left: {
    background: 'var(--bisne-dark)',
    color: 'var(--text-inverse)',
    padding: 48,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
  },
  right: {
    padding: '64px 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    width: '100%',
    maxWidth: 380,
  },
}

function AuthHero() {
  return (
    <div style={STYLES.left}>
      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, opacity: 0.06, pointerEvents: 'none' }}
      >
        {Array.from({ length: 16 }, (_, i) => (
          <line key={`h${i}`} x1={0} y1={i * 48} x2={1200} y2={i * 48} stroke="#FAF7F1" strokeWidth={0.5} />
        ))}
        {Array.from({ length: 12 }, (_, i) => (
          <line key={`v${i}`} x1={i * 64} y1={0} x2={i * 64} y2={1000} stroke="#FAF7F1" strokeWidth={0.5} />
        ))}
      </svg>
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          right: '-15%',
          width: 380,
          height: 380,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(21,184,104,0.35) 0%, transparent 70%)',
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 28, fontFamily: 'Bricolage Grotesque', fontWeight: 800, letterSpacing: '-0.04em' }}>
          bisne
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--bisne-green)', marginLeft: 2, transform: 'translateY(-3px)' }} />
          <span style={{ marginLeft: 8, fontSize: 11, fontFamily: 'DM Sans', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.55 }}>platform</span>
        </div>
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="t-eyebrow" style={{ color: 'var(--bisne-green)', marginBottom: 14 }}>Panel interno</div>
        <h1 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 48, lineHeight: 1.05, letterSpacing: '-0.025em', margin: 0, maxWidth: 480 }}>
          Operá el SaaS desde un solo lugar.
        </h1>
        <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.55, color: 'var(--text-inverse-2)', opacity: 0.85, maxWidth: 460 }}>
          Tenants, billing, soporte, métricas y comunicación. Solo para el equipo Bisne.
        </p>
      </div>
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.55, fontSize: 12.5 }}>
        <span>© 2026 Bisne</span>
        <span className="t-mono">v4.18.2</span>
      </div>
    </div>
  )
}

function AuthErrorBox({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        marginBottom: 14,
        background: 'var(--danger-bg)',
        border: '1px solid var(--danger-border)',
        color: 'var(--danger-fg)',
        fontSize: 13,
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
      }}
    >
      <Icon name="alertCircle" size={14} style={{ marginTop: 2, flexShrink: 0 }} />
      <div>{children}</div>
    </div>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await platformLogin(email, password)
      localStorage.setItem('platform_jwt', res.token)
      toast.success('Login exitoso')
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Credenciales inválidas. Probá de nuevo o restablecé tu contraseña.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page" style={STYLES.page}>
      <AuthHero />
      <div style={STYLES.right}>
        <div style={STYLES.form}>
          <div className="eyebrow t-eyebrow" style={{ marginBottom: 8 }}>Acceso al panel</div>
          <h2 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 28, letterSpacing: '-0.02em', margin: 0 }}>Entrá a tu cuenta</h2>
          <p className="muted-2" style={{ fontSize: 13.5, marginTop: 6 }}>Solo miembros del equipo Bisne. ¿Olvidaste tu contraseña?</p>

          <form onSubmit={submit} style={{ marginTop: 28 }}>
            {error ? <AuthErrorBox>{error}</AuthErrorBox> : null}

            <div className="form-row">
              <label htmlFor="login-email">Email</label>
              <input id="login-email" name="email" className="input" type="email" autoComplete="email" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </div>
            <div className="form-row">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <label htmlFor="login-password">Contraseña</label>
                <a
                  href="/forgot"
                  style={{ fontSize: 12, color: 'var(--bisne-green-800)', fontWeight: 500 }}
                  onClick={(e) => { e.preventDefault(); navigate('/forgot') }}
                >
                  Olvidé mi contraseña
                </a>
              </div>
              <input id="login-password" name="password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••••" />
            </div>

            <AppButton variant="success" size="lg" type="submit" disabled={loading}>
              {loading ? 'Entrando…' : 'Entrar al panel'}
            </AppButton>
          </form>
        </div>
      </div>
    </div>
  )
}
